import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Save, ArrowLeft, Trash2, Eye, EyeOff } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { getToken } from "@/lib/api";
import { ImageField } from "@/components/admin/ImageField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

const emptyPost = {
  title: "", subtitle: "", slug: "", author: "The Studio", excerpt: "",
  body_html: "", featured_image: "", categories: [], tags: [],
  meta_title: "", meta_description: "", og_image: "",
  status: "draft", publish_at: "",
};

const AdminBlogEditor = () => {
  const nav = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === "new";
  const [post, setPost] = useState(emptyPost);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getToken()) { nav("/admin/login"); return; }
    if (isNew) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/blog/${id}`, { headers: authHeader() });
        setPost({ ...emptyPost, ...data, categories: data.categories || [], tags: data.tags || [] });
      } catch { toast.error("Could not load post."); nav("/admin/blog"); }
      finally { setLoading(false); }
    })();
  }, [id, isNew, nav]);

  const quillModules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, 3, 4, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      ["blockquote", "code-block"],
      ["link", "image", "video"],
      [{ align: [] }],
      ["clean"],
    ],
  }), []);

  const update = (k, v) => setPost((p) => ({ ...p, [k]: v }));

  const save = async (asStatus) => {
    if (!post.title.trim()) { toast.error("Title is required."); return; }
    setSaving(true);
    const payload = { ...post, status: asStatus || post.status };
    try {
      if (isNew) {
        const { data } = await axios.post(`${API}/admin/blog`, payload, { headers: authHeader() });
        toast.success("Post created.");
        nav(`/admin/blog/${data.id}`, { replace: true });
      } else {
        const { data } = await axios.put(`${API}/admin/blog/${id}`, payload, { headers: authHeader() });
        setPost({ ...emptyPost, ...data, categories: data.categories || [], tags: data.tags || [] });
        toast.success(asStatus === "published" ? "Published." : asStatus === "draft" ? "Unpublished." : "Saved.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await axios.delete(`${API}/admin/blog/${id}`, { headers: authHeader() });
      toast.success("Deleted.");
      nav("/admin/blog");
    } catch { toast.error("Delete failed."); }
  };

  const csv = (arr) => (arr || []).join(", ");
  const parseCsv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-ivory/55 bg-midnight">Loading…</div>;

  return (
    <div className="min-h-screen bg-midnight" data-testid="admin-blog-editor">
      <Toaster theme="dark" position="top-right" />
      <header className="border-b border-gold/15 bg-midnight-deep/70 backdrop-blur-md sticky top-0 z-20">
        <div className="wrap py-4 flex flex-wrap items-center justify-between gap-3">
          <button onClick={() => nav("/admin/blog")} className="inline-flex items-center gap-2 text-ivory/70 hover:text-gold text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to posts
          </button>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => save("draft")} disabled={saving} className="btn-ghost text-[10px]" data-testid="blog-save-draft">
              {saving ? "Saving…" : (post.status === "published" ? <><EyeOff className="h-4 w-4" /> Unpublish</> : "Save Draft")}
            </button>
            <button onClick={() => save("published")} disabled={saving} className="btn-gold text-[10px]" data-testid="blog-publish">
              <Eye className="h-4 w-4" /> {saving ? "Saving…" : (post.status === "published" ? "Save" : "Publish")}
            </button>
            {!isNew && (
              <button onClick={remove} className="text-red-400 hover:text-red-300 px-3 py-2 text-[10px]" data-testid="blog-delete">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="wrap py-8 grid gap-8 lg:grid-cols-12 max-w-6xl">
        <div className="lg:col-span-8 grid gap-5">
          <div className="glass p-6 grid gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-ivory/55">Title</label>
              <input value={post.title} onChange={(e) => update("title", e.target.value)}
                className="w-full mt-1 bg-midnight/60 border border-gold/20 px-3 py-3 text-ivory text-2xl font-display focus:outline-none focus:border-gold"
                data-testid="blog-title" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-ivory/55">Subtitle (optional)</label>
              <input value={post.subtitle} onChange={(e) => update("subtitle", e.target.value)}
                className="w-full mt-1 bg-midnight/60 border border-gold/20 px-3 py-2 text-ivory italic focus:outline-none focus:border-gold" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-ivory/55">Excerpt (short summary)</label>
              <textarea rows={2} value={post.excerpt} onChange={(e) => update("excerpt", e.target.value)}
                className="w-full mt-1 bg-midnight/60 border border-gold/20 px-3 py-2 text-ivory text-sm focus:outline-none focus:border-gold" />
            </div>
          </div>

          <div className="glass p-6">
            <label className="text-[10px] uppercase tracking-[0.25em] text-ivory/55 mb-3 block">Article Body</label>
            <div className="quill-wrap bg-white text-black">
              <ReactQuill
                theme="snow"
                value={post.body_html}
                onChange={(v) => update("body_html", v)}
                modules={quillModules}
                placeholder="Write the article…"
              />
            </div>
            <p className="mt-3 text-[10px] text-ivory/45">
              Toolbar supports headings, bold/italic/underline, bullet & numbered lists, quotes, code blocks, links, images and video embeds.
            </p>
          </div>
        </div>

        <aside className="lg:col-span-4 grid gap-5">
          <div className="glass p-6 grid gap-4">
            <div className="eyebrow">Publish</div>
            <div className="text-sm text-ivory/80">
              Status: <span className={post.status === "published" ? "text-gold" : "text-saffron"}>{post.status}</span>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-ivory/55">Publish date (schedule)</label>
              <input type="datetime-local" value={(post.publish_at || "").slice(0, 16)}
                onChange={(e) => update("publish_at", e.target.value ? new Date(e.target.value).toISOString() : "")}
                className="w-full mt-1 bg-midnight/60 border border-gold/20 px-3 py-2 text-ivory text-sm focus:outline-none focus:border-gold" />
              <p className="text-[10px] text-ivory/45 mt-1">Post will appear publicly only after this date.</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-ivory/55">Author</label>
              <input value={post.author} onChange={(e) => update("author", e.target.value)}
                className="w-full mt-1 bg-midnight/60 border border-gold/20 px-3 py-2 text-ivory text-sm focus:outline-none focus:border-gold" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-ivory/55">URL slug (auto if empty)</label>
              <input value={post.slug} onChange={(e) => update("slug", e.target.value)} placeholder="my-post-slug"
                className="w-full mt-1 bg-midnight/60 border border-gold/20 px-3 py-2 text-ivory text-sm focus:outline-none focus:border-gold" />
            </div>
            {post.slug && post.status === "published" && (
              <a href={`/blog/${post.slug}`} target="_blank" rel="noreferrer" className="btn-ghost text-[10px] justify-center">
                <Eye className="h-3 w-3" /> View live
              </a>
            )}
          </div>

          <div className="glass p-6 grid gap-4">
            <ImageField label="Featured Image" value={post.featured_image} onChange={(v) => update("featured_image", v)} />
          </div>

          <div className="glass p-6 grid gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-ivory/55">Categories (comma-separated)</label>
              <input value={csv(post.categories)} onChange={(e) => update("categories", parseCsv(e.target.value))}
                className="w-full mt-1 bg-midnight/60 border border-gold/20 px-3 py-2 text-ivory text-sm focus:outline-none focus:border-gold" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-ivory/55">Tags (comma-separated)</label>
              <input value={csv(post.tags)} onChange={(e) => update("tags", parseCsv(e.target.value))}
                className="w-full mt-1 bg-midnight/60 border border-gold/20 px-3 py-2 text-ivory text-sm focus:outline-none focus:border-gold" />
            </div>
          </div>

          <div className="glass p-6 grid gap-4">
            <div className="eyebrow">SEO</div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-ivory/55">Meta Title</label>
              <input value={post.meta_title} onChange={(e) => update("meta_title", e.target.value)}
                className="w-full mt-1 bg-midnight/60 border border-gold/20 px-3 py-2 text-ivory text-sm focus:outline-none focus:border-gold" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-ivory/55">Meta Description</label>
              <textarea rows={2} value={post.meta_description} onChange={(e) => update("meta_description", e.target.value)}
                className="w-full mt-1 bg-midnight/60 border border-gold/20 px-3 py-2 text-ivory text-sm focus:outline-none focus:border-gold" />
            </div>
            <ImageField label="Open Graph Image (social share)" value={post.og_image} onChange={(v) => update("og_image", v)} />
          </div>
        </aside>
      </main>
    </div>
  );
};

export default AdminBlogEditor;
